<template>
  <div
    v-if="modalActions"
    key="footer"
    class="dvp-modal__footer"
    :class="{ 'the-grid': ['maxi', 'full'].includes(size) }"
  >
    <div
      v-if="type !== 'raw'"
      class="dvp-modal__actions appearance-positive"
    >
      <div
        v-if="type === 'transactional'"
        class="transactional"
      >
        <slot name="transactional">
          <basic-button
            :data-track-as="$props.primaryTrackAs"
            :data-track-text="transactionalPrimaryLabel"
            :label="transactionalPrimaryLabel"
            @click="$emit('click-primary')"
          />
          <basic-button
            hierarchy="secondary"
            :data-track-as="$props.secondaryTrackAs"
            :data-track-text="transactionalSecondaryLabel"
            :label="transactionalSecondaryLabel"
            @click="$emit('click-secondary')"
          />
        </slot>
      </div>

      <div
        v-else-if="type === 'acknowledgement'"
        class="acknowledgement"
      >
        <slot name="acknowledgement">
          <basic-button
            :label="acknowledgementLabel"
            :data-track-as="$props.primaryTrackAs"
            :data-track-text="acknowledgementLabel"
            @click="$emit('click-primary')"
          />
        </slot>
      </div>

      <div
        v-else-if="type === 'progress'"
        class="progress"
      >
        <slot name="progress">
          <div class="progress__navigation">
            <basic-button
              v-if="$props.primaryVisible"
              :data-track-as="$props.primaryTrackAs"
              :data-track-text="progressNextLabel"
              :label="progressNextLabel"
              @click="$emit('click-primary')"
            />
            <basic-button
              v-if="$props.secondaryVisible"
              hierarchy="secondary"
              :data-track-as="$props.secondaryTrackAs"
              :data-track-text="progressBackLabel"
              :label="progressBackLabel"
              @click="$emit('click-secondary')"
            />
          </div>
          <basic-button
            hierarchy="tertiary"
            :label="progressCancelLabel"
            @click="$emit('click-tertiary')"
          />
        </slot>
      </div>

      <div
        v-else-if="type === 'passive'"
        class="passive"
      >
        <slot name="passive">
          <basic-button
            hierarchy="primary"
            :label="$t('form.close')"
            @click="close()"
          />
        </slot>
      </div>
    </div>

    <div
      v-else
      class="dvp-modal__actions appearance-positive raw"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue'

  import useI18n from '@shared/hooks/useI18n'

  import BasicButton from '@shared/components/Basic/Button'

  // HOOKS
  defineEmits(['click-primary', 'click-secondary', 'click-tertiary', 'close'])
  const { t } = useI18n()
  const close = inject('close')
  const modalActions = inject('modalActions')
  const size = inject('size')
  const type = inject('type')
  const props = defineProps({
    primaryLabel: {
      type: String,
      default: null,
    },

    primaryTrackAs: {
      type: String,
      default: null,
    },

    primaryVisible: {
      type: Boolean,
      default: true,
    },

    secondaryLabel: {
      type: String,
      default: null,
    },

    secondaryTrackAs: {
      type: String,
      default: null,
    },

    secondaryVisible: {
      type: Boolean,
      default: true,
    },

    tertiaryLabel: {
      type: String,
      default: null,
    },
  })

  // COMPUTED
  const acknowledgementLabel = computed(() => {
    return props.primaryLabel || 'Accept' // TODO: CONTENT
  })

  const progressBackLabel = computed(() => {
    return props.secondaryLabel || t('form.back')
  })

  const progressCancelLabel = computed(() => {
    return props.tertiaryLabel || t('form.cancel')
  })

  const progressNextLabel = computed(() => {
    return props.primaryLabel || t('form.next')
  })

  const transactionalPrimaryLabel = computed(() => {
    return props.primaryLabel || t('form.save')
  })

  const transactionalSecondaryLabel = computed(() => {
    return props.secondaryLabel || t('form.cancel')
  })
</script>

<style scoped>
.dvp-modal__footer.the-grid > * {
  grid-column: 1 / -1;
}

.dvp-modal__footer.the-grid .dvp-modal__actions {
  padding: var(--fixed-spacing-fix-02) 0 var(--dotcom-responsive-gutter-responsive);
}

.dvp-modal__actions {
  padding: var(--fixed-spacing-fix-02) var(--dotcom-responsive-gutter-responsive)
    var(--dotcom-responsive-gutter-responsive);

  .transactional,
  .passive {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-02);
  }

  .acknowledgement {
    display: flex;
    justify-content: center;
  }

  .progress {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-02);

    &__navigation {
      display: flex;
      flex-direction: column;
      gap: var(--fixed-spacing-fix-02);
    }
  }

  &.raw {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-02);
    justify-content: space-between;
  }
}

@media (--v-medium) {
  .dvp-modal {
    &--mini {
      width: 320px;
      margin: 0 auto;
    }

    &__actions {
      .passive,
      .acknowledgement {
        justify-content: flex-end;
      }

      .passive {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .transactional,
      &.raw {
        flex-direction: row-reverse;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: var(--fixed-spacing-fix-06);
      }
    }
  }
}

@media (--v-large) {
  .dvp-modal {
    &--midi {
      grid-column: 3 / 11;
    }

    &__actions {
      .progress {
        flex-direction: row-reverse;
        justify-content: space-between;
        gap: var(--fixed-spacing-fix-06);

        &__navigation {
          flex-direction: row-reverse;
          gap: var(--fixed-spacing-fix-06);
        }
      }
    }
  }
}

@media (--v-wide) {
  .dvp-modal {
    &--mini {
      width: auto;
      margin: 0;
      grid-column: 5 / 9;
    }
  }
}
</style>
